import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Check, X, Sparkles, Crown, Zap, Star, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tiers = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 0,
    yearlyPrice: 0,
    icon: Zap,
    description: 'Learn your real recipe costs for free.',
    features: [
      { name: 'Up to 15 ingredients', included: true },
      { name: 'Up to 3 recipes', included: true },
      { name: 'Ingredient pack-price costing', included: true },
      { name: 'Cost per g / ml / unit', included: true },
      { name: 'Batch, unit & retail pricing', included: true },
      { name: 'Gross profit & margin %', included: true },
      { name: 'Labour & overhead costing', included: false },
      { name: 'Wholesale pricing', included: false },
      { name: 'Analytics & comparisons', included: false },
      { name: 'CSV export', included: false },
    ],
    highlight: false,
    canPurchase: false
  },
  {
    id: 'home_baker',
    name: 'Home Baker',
    monthlyPrice: 7.99,
    yearlyPrice: 79.90,
    icon: Sparkles,
    description: 'For home bakers ready to include their time and business costs.',
    features: [
      { name: 'Up to 50 ingredients', included: true },
      { name: 'Up to 20 recipes', included: true },
      { name: 'Everything in Starter', included: true },
      { name: 'Labour & time costing', included: true },
      { name: 'Overhead costing', included: true },
      { name: 'Recipe scaling', included: true },
      { name: 'Packaging & other direct costs', included: true },
      { name: 'CSV export', included: true },
    ],
    highlight: true,
    canPurchase: true
  },
  {
    id: 'pro_baker',
    name: 'Pro Baker',
    monthlyPrice: 19,
    yearlyPrice: 190,
    icon: Crown,
    description: 'For established businesses who need wholesale pricing and profit insights.',
    features: [
      { name: 'Up to 200 ingredients', included: true },
      { name: 'Up to 100 recipes', included: true },
      { name: 'Everything in Home Baker', included: true },
      { name: 'Wholesale pricing', included: true },
      { name: 'Profitability comparisons', included: true },
      { name: 'Analytics & insights', included: true },
      { name: 'Ingredient price history', included: true },
      { name: 'Margin warnings', included: true },
    ],
    highlight: false,
    canPurchase: true
  },
  {
    id: 'bakery_business',
    name: 'Bakery Business',
    monthlyPrice: 29,
    yearlyPrice: 290,
    icon: Star,
    description: 'For growing bakeries with larger recipe catalogues.',
    features: [
      { name: 'Unlimited ingredients', included: true },
      { name: 'Unlimited recipes', included: true },
      { name: 'Everything in Pro Baker', included: true },
      { name: 'Priority support', included: true },
      { name: 'Multi-user access coming soon', included: true, comingSoon: true },
    ],
    highlight: false,
    canPurchase: true
  }
];

export const PricingPlans = ({ publicMode = false }) => {
  const { subscription, refresh } = useSubscription();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const currentTier = subscription?.tier || 'starter';

  const planNameToTier = {
    'Starter': 'starter',
    'Home Baker': 'home_baker',
    'Pro Baker': 'pro_baker',
    'Bakery Business': 'bakery_business'
  };
  const effectiveTier = planNameToTier[subscription?.plan_name] || currentTier;

  const handleUpgrade = async (tierId) => {
    if (loadingPlan) return;
    
    setLoadingPlan(tierId);
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: tierId,
        origin_url: window.location.origin,
        billing_interval: billingInterval
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoadingPlan(null);
    }
  };

  const getPrice = (tier) => {
    if (tier.monthlyPrice === 0) return 'Free';
    const val = billingInterval === 'yearly' ? tier.yearlyPrice : tier.monthlyPrice;
    const formatted = Number.isInteger(val) ? val : val.toFixed(2);
    return `$${formatted}`;
  };

  const getPriceDetail = (tier) => {
    if (tier.monthlyPrice === 0) return '';
    if (billingInterval === 'yearly') {
      const monthlyEquiv = (tier.yearlyPrice / 12).toFixed(2);
      return `AUD/year (~$${monthlyEquiv}/mo)`;
    }
    return 'AUD/month';
  };

  const getSavings = (tier) => {
    if (tier.monthlyPrice === 0) return null;
    const yearlyIfMonthly = tier.monthlyPrice * 12;
    const savings = yearlyIfMonthly - tier.yearlyPrice;
    if (savings > 0) {
      return Math.round((savings / yearlyIfMonthly) * 100);
    }
    return null;
  };

  return (
    <div className="py-8">
      {/* Current Subscription Status */}
      {!publicMode && subscription && (
        <div className="bg-white rounded-xl border border-[#DBEAFE] p-6 mb-8 max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Subscription
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                subscription.plan_name === 'Bakery Business' ? 'bg-amber-100 text-amber-700' :
                subscription.plan_name === 'Pro Baker' ? 'bg-purple-100 text-purple-700' :
                subscription.plan_name === 'Home Baker' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {subscription.plan_name || subscription.tier_name || 'Starter'}
              </span>
              {subscription.is_trial && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  <Clock className="w-3 h-3 mr-1" />
                  Trial
                </span>
              )}
            </div>
            {subscription.is_trial && subscription.trial_end_date && (
              <div className="text-right">
                <p className={`text-sm font-medium ${subscription.trial_days_remaining <= 3 ? 'text-red-600' : 'text-amber-600'}`}>
                  {subscription.trial_days_remaining} days remaining
                </p>
                <p className="text-xs text-[#6B7280]">
                  Trial ends: {new Date(subscription.trial_end_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div className="bg-[#F9FAFB] rounded-lg p-3">
              <p className="text-[#6B7280]">Ingredients</p>
              <p className="font-semibold">
                {subscription.limits?.current_ingredients || 0} / {subscription.limits?.max_ingredients === -1 ? '∞' : subscription.limits?.max_ingredients}
              </p>
            </div>
            <div className="bg-[#F9FAFB] rounded-lg p-3">
              <p className="text-[#6B7280]">Recipes</p>
              <p className="font-semibold">
                {subscription.limits?.current_recipes || 0} / {subscription.limits?.max_recipes === -1 ? '∞' : subscription.limits?.max_recipes}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Choose Your Plan
        </h2>
        <p className="text-[#6B7280] mb-6">
          Unlock more features as your business grows
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center bg-[#F9FAFB] rounded-full p-1 border border-[#DBEAFE]">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              billingInterval === 'monthly'
                ? 'bg-[#2C5F7C] text-white shadow-sm'
                : 'text-[#6B7280] hover:text-[#2C5F7C]'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('yearly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              billingInterval === 'yearly'
                ? 'bg-[#2C5F7C] text-white shadow-sm'
                : 'text-[#6B7280] hover:text-[#2C5F7C]'
            }`}
          >
            Yearly
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              billingInterval === 'yearly' ? 'bg-green-400 text-white' : 'bg-green-100 text-green-700'
            }`}>
              Save 17%
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
        {tiers.map((tier) => {
          const Icon = tier.icon;
          const isCurrent = !publicMode && tier.id === effectiveTier;
          const savings = getSavings(tier);
          
          return (
            <div
              key={tier.id}
              className={`relative rounded-2xl border-2 p-5 transition-all ${
                tier.highlight 
                  ? 'border-[#2C5F7C] shadow-lg' 
                  : 'border-[#DBEAFE]'
              } ${isCurrent ? 'bg-[#F9FAFB]' : 'bg-white'}`}
              data-testid={`pricing-tier-${tier.id}`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#2C5F7C] text-white text-xs font-medium rounded-full">
                  RECOMMENDED
                </div>
              )}

              {isCurrent && (
                <div className="absolute -top-3 right-4 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                  CURRENT
                </div>
              )}

              {billingInterval === 'yearly' && savings && !tier.highlight && (
                <div className="absolute -top-3 left-4 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                  Save ${tier.monthlyPrice * 12 - tier.yearlyPrice}
                </div>
              )}

              <div className="text-center mb-4">
                <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${
                  tier.highlight ? 'bg-[#2C5F7C] text-white' : 'bg-[#EFF6FF] text-[#2C5F7C]'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {tier.name}
                </h3>
                <p className="text-xs text-[#6B7280] mt-1">{tier.description}</p>
              </div>

              <div className="text-center mb-4">
                <div className="text-2xl font-bold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {getPrice(tier)}
                </div>
                {getPriceDetail(tier) && (
                  <div className="text-xs text-[#6B7280]">{getPriceDetail(tier)}</div>
                )}
              </div>

              <ul className="space-y-2 mb-4 text-sm">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    {feature.comingSoon ? (
                      <Clock className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                    ) : feature.included ? (
                      <Check className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-[#E5E7EB] flex-shrink-0" />
                    )}
                    <span className={feature.comingSoon ? 'text-[#B45309] italic' : feature.included ? 'text-[#1F2937]' : 'text-[#9CA3AF]'}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                className={`w-full rounded-full text-sm ${
                  isCurrent
                    ? 'bg-[#EFF6FF] text-[#2C5F7C] hover:bg-[#DBEAFE]'
                    : (publicMode || tier.canPurchase)
                    ? 'bg-[#2C5F7C] hover:bg-[#1E4A60] text-white'
                    : 'border border-[#DBEAFE] bg-white hover:bg-[#F9FAFB] text-[#6B7280]'
                }`}
                onClick={() =>
                  publicMode
                    ? navigate(tier.id === 'starter' ? '/auth?mode=signup' : `/auth?mode=signup&plan=${tier.id}`)
                    : (!isCurrent && tier.canPurchase && handleUpgrade(tier.id))
                }
                disabled={publicMode ? false : (isCurrent || loadingPlan === tier.id || !tier.canPurchase)}
                data-testid={`select-tier-${tier.id}`}
              >
                {publicMode ? (
                  tier.id === 'starter' ? 'Start Free' : 'Get Started'
                ) : loadingPlan === tier.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : isCurrent ? (
                  'Current Plan'
                ) : tier.canPurchase ? (
                  'Upgrade'
                ) : (
                  'Free Plan'
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-8 text-sm text-[#6B7280] max-w-2xl mx-auto space-y-2" data-testid="pricing-footnote">
        <p>
          All prices are in Australian dollars. Cancel anytime. Existing founding customers keep their
          current price while their subscription remains active.
        </p>
        <p>
          Powered by Stripe. Questions? Contact{' '}
          <a href="mailto:hello@costcraft.app" className="text-[#2C5F7C] hover:underline">
            hello@costcraft.app
          </a>
        </p>
      </div>
    </div>
  );
};
